import { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';

/**
 * Conditions Générales d'Utilisation — content lives in i18n under
 * `legal.terms.*` and is fully translated to fr/en/de.
 */
export function TermsPage() {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.title = t('legal.terms.documentTitle');
  }, [t, i18n.language]);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: '0.12em', fontWeight: 700 }}
      >
        {t('legal.terms.overline')}
      </Typography>
      <Typography variant="h3" sx={{ mt: 1, mb: 4 }}>
        {t('legal.terms.title')}
      </Typography>

      <Box sx={{ '& p, & li': { lineHeight: 1.8, color: 'text.primary' } }}>
        <Section title={t('legal.terms.s1.title')}>
          <Typography>{t('legal.terms.s1.p1')}</Typography>
          <Typography>{t('legal.terms.s1.p2')}</Typography>
        </Section>

        <Section title={t('legal.terms.s2.title')}>
          <Typography>{t('legal.terms.s2.p1')}</Typography>
          <Typography>{t('legal.terms.s2.p2')}</Typography>
        </Section>

        <Section title={t('legal.terms.s3.title')}>
          <Typography>{t('legal.terms.s3.p1')}</Typography>
          <BulletsKey k="legal.terms.s3.bullets" />
          <Typography>{t('legal.terms.s3.p2')}</Typography>
        </Section>

        <Section title={t('legal.terms.s4.title')}>
          <Typography>{t('legal.terms.s4.p1')}</Typography>
          <Typography>{t('legal.terms.s4.p2')}</Typography>
        </Section>

        <Section title={t('legal.terms.s5.title')}>
          <BulletsKey k="legal.terms.s5.bullets" />
        </Section>

        <Section title={t('legal.terms.s6.title')}>
          <BulletsKey k="legal.terms.s6.bullets" />
        </Section>

        <Section title={t('legal.terms.s7.title')}>
          <Typography>{t('legal.terms.s7.p1')}</Typography>
          <Typography>{t('legal.terms.s7.p2')}</Typography>
        </Section>

        <Section title={t('legal.terms.s8.title')}>
          <Typography>{t('legal.terms.s8.p1')}</Typography>
          <Typography>{t('legal.terms.s8.p2')}</Typography>
        </Section>

        <Section title={t('legal.terms.s9.title')}>
          <Typography>{t('legal.terms.s9.p1')}</Typography>
          <Typography>{t('legal.terms.s9.p2')}</Typography>
        </Section>

        <Section title={t('legal.terms.s10.title')}>
          <Typography>{t('legal.terms.s10.p1')}</Typography>
          <Typography>{t('legal.terms.s10.p2')}</Typography>
        </Section>

        <Section title={t('legal.terms.s11.title')}>
          <Typography>
            {t('legal.terms.s11.beforeLink')}
            <Link
              component={RouterLink}
              to="/privacy"
              sx={{ color: 'primary.main', fontWeight: 500 }}
            >
              {t('legal.terms.s11.linkText')}
            </Link>
            {t('legal.terms.s11.afterLink')}
          </Typography>
        </Section>

        <Section title={t('legal.terms.s12.title')}>
          <Typography>{t('legal.terms.s12.p1')}</Typography>
        </Section>

        <Section title={t('legal.terms.s13.title')}>
          <Typography>{t('legal.terms.s13.p1')}</Typography>
        </Section>

        <Section title={t('legal.terms.s14.title')}>
          <Typography>{t('legal.terms.s14.p1')}</Typography>
          <Box component="ul" sx={{ pl: 3, my: 1, listStyle: 'none' }}>
            <li>📧 <Mail to="info@winorgroup.com" /></li>
            <li>📍 {t('legal.contact.city')}</li>
            <li>🏢 {t('legal.contact.publisher')}</li>
          </Box>
        </Section>
      </Box>
    </Container>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <Divider sx={{ my: 4 }} />
      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 700 }}>
        {title}
      </Typography>
      <Stack spacing={1}>{children}</Stack>
    </>
  );
}

function BulletsKey({ k }: { k: string }) {
  const { t } = useTranslation();
  const items = t(k, { returnObjects: true }) as string[];
  return (
    <Box component="ul" sx={{ pl: 3, my: 1 }}>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </Box>
  );
}

function Mail({ to }: { to: string }) {
  return (
    <Link href={`mailto:${to}`} sx={{ color: 'primary.main', fontWeight: 500 }}>
      {to}
    </Link>
  );
}
